import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { dictionary, localizePath } from "@/i18n/translations";
import { api, buildAuthHeaders } from "@/lib/api";
import { setDocumentMeta } from "@/lib/seo";

export default function DashboardLeadDetailsPage({ lang }) {
  const copy = dictionary[lang];
  const { session, user } = useAuth();
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [lead, setLead] = useState(null);

  useEffect(() => {
    setDocumentMeta({
      lang,
      title: `${copy.dashboardLeads.leadDetailsTitle} | ${copy.nav.brand}`,
      description: copy.dashboardLeads.subtitle,
    });
  }, [copy.dashboardLeads.leadDetailsTitle, copy.dashboardLeads.subtitle, copy.nav.brand, lang]);

  const statusLabel = (value) => {
    if (value === "new") return copy.dashboardLeads.statusNew;
    if (value === "in_progress") return copy.dashboardLeads.statusInProgress;
    return copy.dashboardLeads.statusClosed;
  };

  const fetchLead = async () => {
    if (user?.role !== "admin") {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/admin/leads/${leadId}`, {
        headers: buildAuthHeaders(session),
      });
      setLead(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || copy.common.requestFailed);
      setLead(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLead();
  }, [leadId, user?.role]);

  const goBack = () => {
    const backQuery = searchParams.get("back");
    const base = localizePath(lang, "/dashboard/leads");
    navigate(backQuery ? `${base}?${backQuery}` : base);
  };

  const onStatusChange = async (status) => {
    if (!lead) return;
    setUpdating(true);
    try {
      const response = await api.patch(
        `/admin/leads/${lead.id}/status`,
        { status },
        { headers: buildAuthHeaders(session) },
      );
      setLead(response.data);
      toast.success(copy.dashboardLeads.statusUpdated);
    } catch (error) {
      toast.error(error?.response?.data?.detail || copy.common.requestFailed);
    } finally {
      setUpdating(false);
    }
  };

  if (user?.role !== "admin") {
    return (
      <Card className="border-gold-500/20 bg-white/90" data-testid="dashboard-lead-details-forbidden-card">
        <CardHeader>
          <CardTitle data-testid="dashboard-lead-details-forbidden-title">{copy.dashboardLeads.leadDetailsTitle}</CardTitle>
        </CardHeader>
        <CardContent data-testid="dashboard-lead-details-forbidden-message">{copy.dashboardLeads.adminOnly}</CardContent>
      </Card>
    );
  }

  if (loading) {
    return <p data-testid="dashboard-lead-details-loading">{copy.common.loading}</p>;
  }

  if (!lead) {
    return (
      <Card className="border-gold-500/20 bg-white/90" data-testid="dashboard-lead-details-not-found-card">
        <CardHeader>
          <CardTitle data-testid="dashboard-lead-details-not-found-title">{copy.dashboardLeads.notFound}</CardTitle>
        </CardHeader>
        <CardContent>
          <Button onClick={goBack} data-testid="dashboard-lead-details-back-button">
            {copy.dashboardLeads.backToLeads}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-lead-details-page">
      <section className="flex flex-wrap items-center justify-between gap-3" data-testid="dashboard-lead-details-header">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold text-stone-900 sm:text-4xl" data-testid="dashboard-lead-details-title">
            {copy.dashboardLeads.leadDetailsTitle}
          </h1>
          <p className="text-sm text-stone-600" data-testid="dashboard-lead-details-subtitle">
            {copy.dashboardLeads.detailSubtitle}
          </p>
        </div>

        <Button variant="outline" onClick={goBack} data-testid="dashboard-lead-details-back-link-button">
          {copy.dashboardLeads.backToLeads}
        </Button>
      </section>

      <Card className="border-gold-500/20 bg-white/95" data-testid="dashboard-lead-details-card">
        <CardContent className="grid gap-6 p-5 lg:grid-cols-2">
          <div className="space-y-3" data-testid="dashboard-lead-details-main-info">
            <p className="text-sm text-stone-500" data-testid="dashboard-lead-details-created-date">{lead.created_at?.split("T")?.[0]}</p>
            <p className="text-2xl font-semibold text-stone-900" data-testid="dashboard-lead-details-name">{lead.full_name}</p>
            <p className="text-sm text-stone-700" data-testid="dashboard-lead-details-email">{lead.email}</p>
            <p className="text-sm text-stone-700" data-testid="dashboard-lead-details-phone">{lead.phone}</p>
            <Badge variant="outline" data-testid="dashboard-lead-details-source-badge">{copy.dashboardLeads.sourceContactPage}</Badge>
          </div>

          <div className="space-y-3" data-testid="dashboard-lead-details-status-section">
            <label className="text-xs text-stone-500" data-testid="dashboard-lead-details-status-label">{copy.dashboardLeads.statusLabel}</label>
            <Select value={lead.status} onValueChange={onStatusChange} disabled={updating}>
              <SelectTrigger data-testid="dashboard-lead-details-status-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new" data-testid="dashboard-lead-details-status-new">{copy.dashboardLeads.statusNew}</SelectItem>
                <SelectItem value="in_progress" data-testid="dashboard-lead-details-status-in-progress">{copy.dashboardLeads.statusInProgress}</SelectItem>
                <SelectItem value="closed" data-testid="dashboard-lead-details-status-closed">{copy.dashboardLeads.statusClosed}</SelectItem>
              </SelectContent>
            </Select>

            <p className="text-sm text-stone-700" data-testid="dashboard-lead-details-status-value">{statusLabel(lead.status)}</p>
            <div className="rounded-2xl border border-dashed border-gold-500/30 bg-amber-50/50 p-3 text-xs text-stone-600" data-testid="dashboard-lead-details-whatsapp-placeholder">
              {copy.dashboardLeads.whatsappPlaceholder}
            </div>
          </div>

          <div className="rounded-2xl bg-stone-100 p-4 lg:col-span-2" data-testid="dashboard-lead-details-message-block">
            <p className="text-xs text-stone-500" data-testid="dashboard-lead-details-message-label">{copy.contactPage.message}</p>
            <p className="mt-2 whitespace-pre-wrap text-sm text-stone-800" data-testid="dashboard-lead-details-message-value">{lead.message}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
